import { useQuery } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';
import type { components } from '@/api/types/api-gateway';

// Audit log queries. Backed by R5 GET /v1/audit-log (cap org.audit_log).

export type AuditEntry = components['schemas']['AuditEntry'];
export type AuditStats = components['schemas']['AuditStats'];

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || !data) {
    const msg = (error as { error?: string } | undefined)?.error ?? `${what} failed`;
    throw new Error(msg);
  }
  return data;
}

export type AuditTargetType =
  | 'user'
  | 'group'
  | 'invitation'
  | 'org'
  | 'alert'
  | 'sensor'
  | 'asset';

export function useAuditLog(
  clients: ApiClients,
  vars: { targetType?: AuditTargetType; limit?: number } = {},
) {
  return useQuery({
    queryKey: ['audit-log', vars.targetType ?? 'all', vars.limit ?? 100],
    queryFn: async (): Promise<AuditEntry[]> => {
      const { data, error } = await clients.apiGateway.GET('/v1/audit-log', {
        params: {
          query: {
            ...(vars.targetType ? { target_type: vars.targetType } : {}),
            ...(vars.limit ? { limit: vars.limit } : {}),
          },
        },
      });
      const body = unwrap(data, error, 'audit-log');
      return body.items;
    },
    staleTime: 15_000,
  });
}

export function useAuditLogStats(
  clients: ApiClients,
  vars: { windowHours?: number } = {},
) {
  return useQuery({
    queryKey: ['audit-log', 'stats', vars.windowHours ?? 24],
    queryFn: async (): Promise<AuditStats> => {
      const { data, error } = await clients.apiGateway.GET('/v1/audit-log/stats', {
        params: {
          query: vars.windowHours ? { window_hours: vars.windowHours } : {},
        },
      });
      return unwrap(data, error, 'audit-log/stats');
    },
    staleTime: 15_000,
  });
}
